import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckIcon,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Info,
  Loader2,
  SplitSquareHorizontal,
  TrendingUp,
} from 'lucide-react';
import { useSelector } from 'react-redux';

import {
  Alert,
  AlertDescription,
  Badge,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Label,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Button,
  cn,
} from '@design-system';
import { CurrencyInput, IntegerInput, PercentInput } from '@client/shared/components/numeric-input';
import {
  formatCurrency,
  formatLtcCostDisplay,
  carePhaseDefList,
  isIncomeStreamAccountType,
} from '@shared';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { selectClient } from '@client/model/selectors';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';

import {
  type AssetTypeId,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  getFieldLabels,
  getCalcFormulaInfo,
} from '@/features/guided-financial-planning/config';
import {
  YEAR_PRESETS,
  CURRENT_YEAR,
  YearPresetSelect,
  CarePhaseYearSelect,
  type CarePhaseYears,
} from './year-select';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  isSelfFundingFormSavable,
  useSelfFundingForm,
} from '@/features/guided-financial-planning/hooks';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { DEFAULT_CUSTOM_CLIENT_PERCENT } from '@/features/guided-financial-planning/hooks';
import { CalcFormulaSheetContent } from './calc-formula-sheet-content';
import { PartnerAllocationSection, type AllocationOption } from './partner-allocation-section';

type SaveState = 'idle' | 'saving' | 'saved';

function RequiredFieldLabel({ text }: { text: string }): ReactNode {
  return (
    <>
      {text}
      <span
        className="ml-0.5 text-red-500"
        aria-hidden
      >
        *
      </span>
    </>
  );
}

function LabelWithTooltip({ children, tooltip }: { children: ReactNode; tooltip: ReactNode }) {
  return (
    <>
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="text-muted-foreground ml-1 inline-block size-3.5 -translate-y-px cursor-help align-middle" />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs text-xs font-normal normal-case"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </>
  );
}

function LoadingOverlay({ state }: { state: SaveState }) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'bg-background/90 absolute inset-0 z-10 flex scale-105 flex-col items-center justify-center rounded-lg backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <p className="text-foreground text-sm font-medium">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

export interface AllocationCallbacks {
  hasPartner: boolean;
  clientName: string;
  partnerName: string;
  initialState: {
    enabled: boolean;
    option: AllocationOption;
    customClientPercent: number;
    originalValue?: number;
    originalMonetaryValues?: Record<string, number>;
  };
  onApply: (
    state: {
      enabled: boolean;
      option: AllocationOption;
      customClientPercent: number;
      formValue?: number;
    },
    overrides?: {
      fundingSourceId: string;
      sourceData: SelfFundingSource;
    },
  ) => Promise<void>;
}

interface AccountCardProps {
  assetTypeId: AssetTypeId;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  fundingSourceId: string | null;
  existingSource?: SelfFundingSource;
  onSaveComplete: () => void;
  disabled?: boolean;
  allocationAmount?: number;
  lockedByPartnerName?: string;
  allocation?: AllocationCallbacks;
  hideHeader?: boolean;
}

function isSourceComplete(source?: SelfFundingSource): boolean {
  if (!source) return false;
  if (isIncomeStreamAccountType(source.accountType)) {
    return source.existingAssetsValue !== null && source.gainsTaxRate !== null;
  }
  return (
    source.existingAssetsValue !== null &&
    source.annualRateOfReturn !== null &&
    source.gainsTaxRate !== null
  );
}

export function AccountCard({
  assetTypeId,
  label,
  isExpanded,
  onToggle,
  fundingSourceId,
  existingSource,
  onSaveComplete,
  disabled,
  allocationAmount,
  lockedByPartnerName,
  allocation,
  hideHeader,
}: AccountCardProps) {
  const isLocked = !!lockedByPartnerName;
  const isComplete = isSourceComplete(existingSource);
  const {
    form,
    onSubmit,
    buildSourceData,
    isSaving,
    clientId,
    isIncomeStream,
    currentAge,
    ltcEndAge,
  } = useSelfFundingForm(assetTypeId, fundingSourceId, existingSource);
  const client = useSelector(selectClient);
  const formValues = useWatch({ control: form.control });
  const incomeReferenceAgeValue = (formValues as { incomeReferenceAge?: number | null })
    .incomeReferenceAge;
  const incomeReferenceAgeError = useMemo(() => {
    if (!isIncomeStream) return undefined;
    if (typeof incomeReferenceAgeValue !== 'number') return undefined;
    if (currentAge != null && incomeReferenceAgeValue < currentAge) {
      return `Must be at least ${currentAge} (current age)`;
    }
    if (ltcEndAge != null && incomeReferenceAgeValue > ltcEndAge) {
      return `Must be at most ${ltcEndAge} (end of care)`;
    }
    return undefined;
  }, [isIncomeStream, incomeReferenceAgeValue, currentAge, ltcEndAge]);
  const canSave = useMemo(
    () => isSelfFundingFormSavable(formValues) && !incomeReferenceAgeError,
    [formValues, incomeReferenceAgeError],
  );
  const watchedValues = useWatch({
    control: form.control,
    name: [
      'existingAssetsValue',
      'yearlyContribution',
      'annualRateOfReturn',
      'gainsTaxRate',
      'incomeReferenceAge',
    ],
  });
  const [
    existingAssetsValue,
    yearlyContribution,
    annualRateOfReturn,
    gainsTaxRate,
    incomeReferenceAge,
  ] = watchedValues ?? [];

  const contributionStopYear = form.watch('contributionStopYear');
  const [hasContributionStopToggle, setHasContributionStopToggle] = useState(
    () => contributionStopYear != null,
  );
  const hasDrawdown = form.watch('hasDrawdown');
  const drawdownStartAtYear = form.watch('drawdownStartAtYear');
  const drawdownEndAtYear = form.watch('drawdownEndAtYear');
  const drawdownAmount = form.watch('drawdownAmount');
  const drawdownAmountInterval = form.watch('drawdownAmountInterval');

  const selfFundingStateForPreview = useMemo((): Partial<SelfFundingSource> | null => {
    if (isIncomeStream) {
      if (existingAssetsValue == null || gainsTaxRate == null) return null;
      return {
        existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
        monthlyContributionAmount: null,
        annualRateOfReturn: typeof annualRateOfReturn === 'number' ? annualRateOfReturn : 0,
        gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
        monthlyIncome: null,
        incomeReferenceAge: typeof incomeReferenceAge === 'number' ? incomeReferenceAge : null,
        accountType: ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId] ?? null,
      };
    }

    if (existingAssetsValue == null || annualRateOfReturn == null || gainsTaxRate == null) {
      return null;
    }
    return {
      existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
      monthlyContributionAmount:
        yearlyContribution != null && typeof yearlyContribution === 'number'
          ? yearlyContribution / 12
          : null,
      annualRateOfReturn: typeof annualRateOfReturn === 'number' ? annualRateOfReturn : null,
      gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
      monthlyIncome: existingSource?.monthlyIncome ?? null,
      accountType: ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId] ?? null,
      contributionStopYear: contributionStopYear ?? null,
      hasDrawdown: hasDrawdown ?? false,
      drawdownStartAtYear: hasDrawdown ? (drawdownStartAtYear ?? null) : null,
      drawdownEndAtYear: hasDrawdown ? (drawdownEndAtYear ?? null) : null,
      drawdownAmount: hasDrawdown ? (drawdownAmount ?? null) : null,
      drawdownAmountInterval: hasDrawdown ? (drawdownAmountInterval ?? 'yearly') : null,
    };
  }, [
    isIncomeStream,
    existingAssetsValue,
    yearlyContribution,
    annualRateOfReturn,
    gainsTaxRate,
    incomeReferenceAge,
    existingSource?.monthlyIncome,
    assetTypeId,
    contributionStopYear,
    hasDrawdown,
    drawdownStartAtYear,
    drawdownEndAtYear,
    drawdownAmount,
    drawdownAmountInterval,
  ]);

  const { ltcAtYear, yearsTillLtc, ltcAtAge } = client?.appliedInferenceSet ?? {};
  const clientBirthDate = client?.intakeSurvey?.clientBirthDate;
  const carePhaseYears: CarePhaseYears = useMemo(() => {
    const startYears = client?.clientPhasePredictedStartYears;
    if (!startYears) return { earlyCare: null, moderateCare: null, fullCare: null };
    return {
      earlyCare: startYears[carePhaseDefList[0].value] ?? null,
      moderateCare: startYears[carePhaseDefList[1].value] ?? null,
      fullCare: startYears[carePhaseDefList[2].value] ?? null,
    };
  }, [client?.clientPhasePredictedStartYears]);
  const allPhaseCareMonthsNeeded = client?.allPhaseCosts?.allPhaseCareMonthsNeeded;
  const ltcEndYear = useMemo(() => {
    if (!ltcAtYear || !allPhaseCareMonthsNeeded) return null;
    return Math.round(ltcAtYear + allPhaseCareMonthsNeeded / 12);
  }, [ltcAtYear, allPhaseCareMonthsNeeded]);

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingStateForPreview, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const { flags } = useFeatureFlags();
  const roundingStep = useLtcCostDisplayPrecision();
  const showPreview = flags.selfFundingGrowthPreview === true && projectedValue != null;

  const currentValueFormatted = formatCompactCurrency(
    typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
  );
  const previewTooltipCopy = isIncomeStream
    ? 'Total after-tax retirement income expected over the entire care period, starting at predicted long-term care age. The annual growth % is a cost-of-living adjustment applied to your income, not an investment return.'
    : 'Current account value and projected value at predicted long-term care age, based on your growth and tax assumptions.';

  const previewBadgeContent = showPreview ? (
    isIncomeStream ? (
      <>
        <span className="inline-flex items-center font-semibold">
          <TrendingUp className="mr-1 size-3.5 shrink-0" />
          {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
        </span>
        <span className="ml-1 text-gray-700">total (age {ageAtLtc ?? ltcAtYear})</span>
      </>
    ) : (
      <>
        <span className="text-gray-700">
          {
            formatLtcCostDisplay(
              typeof existingAssetsValue === 'number' ? existingAssetsValue : undefined,
              { roundingStep },
            ).formattedDisplay
          }
        </span>
        <span className="mx-1"></span>
        <span className="inline-flex items-center font-semibold">
          <TrendingUp className="mr-1 size-3.5 shrink-0" />
          {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
        </span>
        <span className="ml-1 text-gray-700">(age {ageAtLtc ?? ltcAtYear})</span>
      </>
    )
  ) : null;

  const fieldLabels = getFieldLabels(assetTypeId);
  if (isIncomeStream) {
    const refAge = typeof incomeReferenceAge === 'number' ? incomeReferenceAge : ltcAtAge;
    if (refAge != null) {
      fieldLabels.currentValue = {
        ...fieldLabels.currentValue,
        label:
          refAge === ltcAtAge
            ? `${fieldLabels.currentValue.label} (age ${refAge})`
            : `Annual income at age ${refAge}`,
      };
    }
  }

  const isSessionComplete = useIsSessionComplete();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const saveCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Allocation section state
  const [allocationEnabled, setAllocationEnabled] = useState(
    allocation?.initialState.enabled ?? false,
  );
  const [allocationOption, setAllocationOption] = useState<AllocationOption>(
    allocation?.initialState.option ?? 'mirror',
  );
  const [customClientPercent, setCustomClientPercent] = useState(
    allocation?.initialState.customClientPercent ?? DEFAULT_CUSTOM_CLIENT_PERCENT,
  );

  // Sync allocation state when initial state changes (e.g. session reload)
  const prevAllocationKey = useRef<string | null>(null);
  useEffect(() => {
    if (!allocation) return;
    const key = `${fundingSourceId}-${allocation.initialState.enabled}-${allocation.initialState.option}-${allocation.initialState.customClientPercent}`;
    if (prevAllocationKey.current === key) return;
    prevAllocationKey.current = key;
    setAllocationEnabled(allocation.initialState.enabled);
    setAllocationOption(allocation.initialState.option);
    setCustomClientPercent(allocation.initialState.customClientPercent);

    // Show pre-split totals in the form so user always edits the full amounts
    if (allocation.initialState.enabled && allocation.initialState.originalValue != null) {
      form.setValue('existingAssetsValue', allocation.initialState.originalValue);
    }
    if (allocation.initialState.enabled && allocation.initialState.originalMonetaryValues) {
      const orig = allocation.initialState.originalMonetaryValues;
      if (orig.monthlyContributionAmount != null) {
        form.setValue('yearlyContribution', orig.monthlyContributionAmount * 12);
      }
      if (orig.drawdownAmount != null) {
        form.setValue('drawdownAmount', orig.drawdownAmount);
      }
    }
  }, [allocation, fundingSourceId, form]);

  // After Redux refreshes the source with split values, restore pre-split originals
  useEffect(() => {
    if (!allocation?.initialState.enabled) return;
    const originalValue = allocation.initialState.originalValue;
    if (originalValue == null) return;
    if (
      existingSource?.existingAssetsValue != null &&
      existingSource.existingAssetsValue !== originalValue
    ) {
      form.setValue('existingAssetsValue', originalValue);
    }
    const origMonetary = allocation.initialState.originalMonetaryValues;
    if (origMonetary) {
      if (
        origMonetary.monthlyContributionAmount != null &&
        existingSource?.monthlyContributionAmount != null &&
        existingSource.monthlyContributionAmount !== origMonetary.monthlyContributionAmount
      ) {
        form.setValue('yearlyContribution', origMonetary.monthlyContributionAmount * 12);
      }
      if (
        origMonetary.drawdownAmount != null &&
        existingSource?.drawdownAmount != null &&
        existingSource.drawdownAmount !== origMonetary.drawdownAmount
      ) {
        form.setValue('drawdownAmount', origMonetary.drawdownAmount);
      }
    }
  }, [
    existingSource?.existingAssetsValue,
    existingSource?.monthlyContributionAmount,
    existingSource?.drawdownAmount,
    allocation?.initialState.enabled,
    allocation?.initialState.originalValue,
    allocation?.initialState.originalMonetaryValues,
    form,
  ]);

  // Track save state transitions
  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      saveCompleteTimeoutRef.current = setTimeout(() => {
        setSaveState('idle');
        onSaveComplete();
      }, 800);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, onSaveComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveCompleteTimeoutRef.current) {
        clearTimeout(saveCompleteTimeoutRef.current);
      }
    };
  }, []);

  const [isApplyingAllocation, setIsApplyingAllocation] = useState(false);
  const [showFormulaSheet, setShowFormulaSheet] = useState(false);
  const formulaInfo = getCalcFormulaInfo(assetTypeId);

  const handleSubmit = useCallback(async () => {
    const isFirstSaveWithAllocation = allocation && !fundingSourceId && allocationEnabled;

    if (isFirstSaveWithAllocation) {
      // Validate form first
      const isValid = await form.trigger();
      if (!isValid) return;

      setSaveState('saving');
      setIsApplyingAllocation(true);
      try {
        const formValues = form.getValues();
        const sourceData = buildSourceData(formValues);

        // Save via API directly so we get the fundingSourceId back
        const response = await guidedPlanningApi.upsertSelfFunding({
          clientId,
          fundingSourceId: null,
          fundingSourceData: sourceData,
        });

        // Apply allocation with the newly created fundingSourceId
        await allocation.onApply(
          {
            enabled: allocationEnabled,
            option: allocationOption,
            customClientPercent,
            formValue: formValues.existingAssetsValue,
          },
          {
            fundingSourceId: response.fundingSourceId,
            sourceData,
          },
        );

        setSaveState('saved');
        setTimeout(() => {
          setSaveState('idle');
          onSaveComplete();
        }, 800);
      } catch {
        setSaveState('idle');
      } finally {
        setIsApplyingAllocation(false);
      }
    } else {
      const isValid = await form.trigger();
      if (!isValid) return;

      // When allocation will write the source (linking, unlinking, or ratio change),
      // skip the Redux save to avoid a double-write race against applyCardAllocation.
      const allocationWillWriteSource =
        !!allocation &&
        !!fundingSourceId &&
        !!existingSource &&
        (allocationEnabled || allocation.initialState.enabled);

      if (!allocationWillWriteSource) {
        await form.handleSubmit(onSubmit)();
      }

      if (allocation && fundingSourceId && existingSource) {
        setIsApplyingAllocation(true);
        try {
          const formValues = form.getValues();
          const sourceData = buildSourceData(formValues);
          await allocation.onApply(
            {
              enabled: allocationEnabled,
              option: allocationOption,
              customClientPercent,
              formValue:
                typeof formValues.existingAssetsValue === 'number'
                  ? formValues.existingAssetsValue
                  : undefined,
            },
            {
              fundingSourceId,
              sourceData,
            },
          );
        } finally {
          setIsApplyingAllocation(false);
        }
      }
    }
  }, [
    form,
    onSubmit,
    buildSourceData,
    clientId,
    allocation,
    fundingSourceId,
    existingSource,
    allocationEnabled,
    allocationOption,
    customClientPercent,
    onSaveComplete,
  ]);

  const accountValue =
    typeof existingAssetsValue === 'number'
      ? existingAssetsValue
      : (existingSource?.existingAssetsValue ?? 0);

  const formContent = (
    <>
      <div className="relative">
        <LoadingOverlay state={saveState} />

        {allocationEnabled && allocation?.initialState.originalValue != null && (
          <Alert
            variant="warning"
            className="gap-1! mb-4 rounded-md p-2.5 [&>svg]:translate-y-px"
          >
            <SplitSquareHorizontal className="size-3.5!" />
            <AlertDescription className="text-xs">
              You&apos;re editing the combined total for {allocation.clientName.split(' ')[0]} and{' '}
              {allocation.partnerName.split(' ')[0]}.{' '}
              {allocationOption === 'mirror'
                ? 'Both will receive the full amount when you save.'
                : `The ${allocationOption === 'split-50-50' ? '50/50' : `${customClientPercent}/${100 - customClientPercent}`} split will be applied when you save.`}
            </AlertDescription>
          </Alert>
        )}

        {showPreview && !hideHeader && (
          <Badge
            variant="secondary"
            className="mb-4 w-fit font-normal sm:hidden"
          >
            {isIncomeStream
              ? `Total income during care : `
              : ageAtLtc != null
                ? `Projected value by age ${ageAtLtc} : `
                : `Projected value by ${ltcAtYear} : `}
            <span className="font-semibold">
              {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
            </span>
          </Badge>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="existingAssetsValue"
            control={form.control}
            render={({ field, fieldState }) => (
              <CurrencyInput
                label={
                  <LabelWithTooltip
                    tooltip={
                      isIncomeStream
                        ? 'Expected annual income during long-term care years. If the reference age is earlier than LTC start age, the expected annual growth is applied to project the income forward.'
                        : 'Present-day value of this account or asset'
                    }
                  >
                    <RequiredFieldLabel text={fieldLabels.currentValue.label} />
                  </LabelWithTooltip>
                }
                placeholder={fieldLabels.currentValue.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />

          {!isIncomeStream && (
            <Controller
              name="yearlyContribution"
              control={form.control}
              render={({ field, fieldState }) => (
                <CurrencyInput
                  label={
                    <LabelWithTooltip tooltip="Amount added each year to this account">
                      {fieldLabels.yearlyContribution.label}
                    </LabelWithTooltip>
                  }
                  placeholder={fieldLabels.yearlyContribution.placeholder}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onPreview={field.onChange}
                  errorMessage={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          )}

          {!isIncomeStream && (
            <Controller
              name="annualRateOfReturn"
              control={form.control}
              render={({ field, fieldState }) => (
                <PercentInput
                  label={
                    <LabelWithTooltip tooltip="The expected annual rate of return applied to both existing assets and contributions">
                      <RequiredFieldLabel text={fieldLabels.rateOfReturn.label} />
                    </LabelWithTooltip>
                  }
                  placeholder={fieldLabels.rateOfReturn.placeholder}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onPreview={field.onChange}
                  errorMessage={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          )}

          <div>
            <Controller
              name="gainsTaxRate"
              control={form.control}
              render={({ field, fieldState }) => (
                <PercentInput
                  label={
                    <LabelWithTooltip
                      tooltip={
                        isIncomeStream
                          ? 'The estimated tax rate that applies to this income'
                          : 'The estimated tax rate applied when withdrawing from this account'
                      }
                    >
                      <RequiredFieldLabel text={fieldLabels.taxRate.label} />
                    </LabelWithTooltip>
                  }
                  placeholder={fieldLabels.taxRate.placeholder}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onPreview={field.onChange}
                  errorMessage={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
            {fieldLabels.taxRateHint && (
              <p className="text-muted-foreground mt-1 text-[11px] leading-snug">
                {fieldLabels.taxRateHint}
              </p>
            )}
          </div>
        </div>

        {isIncomeStream && flags.retirementIncomeExpanded && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="incomeReferenceAge"
              control={form.control}
              render={({ field, fieldState }) => (
                <div>
                  <IntegerInput
                    label={
                      <LabelWithTooltip
                        tooltip={
                          ltcAtAge != null
                            ? `The age at which the entered income applies. Defaults to LTC start age (${ltcAtAge}). If earlier, the Expected Annual Growth is applied each year up to LTC start age.`
                            : 'The age at which the entered income applies. If earlier than LTC start age, the Expected Annual Growth is applied each year up to LTC start age.'
                        }
                      >
                        Income start age
                        {ltcAtAge != null && (
                          <span className="text-muted-foreground ml-1">
                            (LTC start age: {ltcAtAge})
                          </span>
                        )}
                      </LabelWithTooltip>
                    }
                    placeholder={ltcAtAge != null ? String(ltcAtAge) : 'Age'}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    onPreview={field.onChange}
                    errorMessage={fieldState.error?.message}
                    disabled={disabled}
                  />
                  {incomeReferenceAgeError && (
                    <p className="mt-1 text-xs text-red-500">{incomeReferenceAgeError}</p>
                  )}
                </div>
              )}
            />

            <div>
              <Controller
                name="annualRateOfReturn"
                control={form.control}
                render={({ field, fieldState }) => (
                  <PercentInput
                    label={
                      <LabelWithTooltip tooltip="The expected annual % increase in your retirement income, typically a cost-of-living adjustment, not an investment return. Compounds each year from the reference age through the end of the care period.">
                        {fieldLabels.rateOfReturn.label}
                      </LabelWithTooltip>
                    }
                    placeholder={fieldLabels.rateOfReturn.placeholder}
                    value={field.value ?? null}
                    onChange={v => field.onChange(v ?? 0)}
                    onPreview={v => field.onChange(v ?? 0)}
                    errorMessage={fieldState.error?.message}
                    disabled={disabled}
                  />
                )}
              />
              <p className="text-muted-foreground mt-1 text-[11px] leading-snug">
                Cost-of-living adjustment to your income — compounds each year from the reference
                age through the end of your care period.
              </p>
            </div>
          </div>
        )}

        {/* ── Advanced Options (Investment: contribution stop + withdrawals) ── */}
        {flags.selfFundingAdvancedOptions && !isIncomeStream && (
          <div className="mt-4 space-y-4">
            {/* ── Contribution End Date Toggle ── */}
            <div className="space-y-2 border-t pt-4">
              <label
                htmlFor="has-contribution-stop"
                className="flex cursor-pointer items-center justify-between"
              >
                <span className="inline-flex items-center gap-1 text-xs font-medium">
                  {fieldLabels.contributionStop}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-xs text-xs font-normal normal-case"
                    >
                      Set a year when assumed contributions to this account will stop, rather than
                      continuing until long-term care begins.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <Switch
                  id="has-contribution-stop"
                  checked={hasContributionStopToggle}
                  onCheckedChange={checked => {
                    setHasContributionStopToggle(checked);
                    if (checked && contributionStopYear == null) {
                      form.setValue('contributionStopYear', CURRENT_YEAR + 5);
                    } else if (!checked) {
                      form.setValue('contributionStopYear', null);
                    }
                  }}
                  disabled={disabled}
                />
              </label>

              {hasContributionStopToggle && (
                <Controller
                  name="contributionStopYear"
                  control={form.control}
                  render={({ field }) => (
                    <YearPresetSelect
                      label="Stop at"
                      defaultLabel="At LTC start (default)"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={disabled}
                    />
                  )}
                />
              )}
            </div>

            {/* ── Withdrawals Toggle ── */}
            <div className="space-y-2 border-t pt-4">
              <label
                htmlFor="has-drawdown"
                className="flex cursor-pointer items-center justify-between"
              >
                <span className="inline-flex items-center gap-1 text-xs font-medium">
                  Withdrawals
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-xs text-xs font-normal normal-case"
                    >
                      Expected withdrawals from this account before care begins. This reduces the
                      projected value available for long-term care funding.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <Controller
                  name="hasDrawdown"
                  control={form.control}
                  render={({ field }) => (
                    <Switch
                      id="has-drawdown"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  )}
                />
              </label>

              {hasDrawdown && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Controller
                      name="drawdownStartAtYear"
                      control={form.control}
                      render={({ field }) => (
                        <YearPresetSelect
                          label="Withdrawals begin"
                          defaultLabel="Starting now"
                          value={field.value}
                          onChange={field.onChange}
                          disabled={disabled}
                        />
                      )}
                    />

                    <Controller
                      name="drawdownEndAtYear"
                      control={form.control}
                      render={({ field }) => (
                        <CarePhaseYearSelect
                          label="Withdrawals end"
                          defaultLabel="At LTC start"
                          value={field.value}
                          onChange={field.onChange}
                          disabled={disabled}
                          carePhaseYears={carePhaseYears}
                          ltcEndYear={ltcEndYear}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="inline-block text-xs font-medium">Withdrawal amount</Label>
                    <div className="flex items-center gap-2">
                      <Controller
                        name="drawdownAmount"
                        control={form.control}
                        render={({ field }) => (
                          <InputGroup className="flex-1">
                            <InputGroupAddon align="inline-start">$</InputGroupAddon>
                            <InputGroupInput
                              inputMode="numeric"
                              placeholder="Enter amount"
                              value={field.value != null ? field.value.toLocaleString('en-US') : ''}
                              onChange={e => {
                                const raw = e.target.value.replace(/[^0-9]/g, '');
                                field.onChange(raw ? Number(raw) : null);
                              }}
                              disabled={disabled}
                            />
                          </InputGroup>
                        )}
                      />
                      <Controller
                        name="drawdownAmountInterval"
                        control={form.control}
                        render={({ field }) => (
                          <ToggleGroup
                            type="single"
                            value={field.value}
                            onValueChange={val => {
                              if (val) field.onChange(val);
                            }}
                            variant="outline"
                            className="shrink-0"
                            disabled={disabled}
                          >
                            <ToggleGroupItem value="monthly">Monthly</ToggleGroupItem>
                            <ToggleGroupItem value="yearly">Yearly</ToggleGroupItem>
                          </ToggleGroup>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Partner allocation section */}
      {allocation?.hasPartner && (
        <PartnerAllocationSection
          enabled={allocationEnabled}
          onEnabledChange={setAllocationEnabled}
          value={accountValue}
          clientName={allocation.clientName}
          partnerName={allocation.partnerName}
          selectedOption={allocationOption}
          onOptionChange={setAllocationOption}
          customClientPercent={customClientPercent}
          onCustomClientPercentChange={setCustomClientPercent}
          disabled={disabled}
          className="mt-4 border-t py-5"
        />
      )}

      {!disabled && (
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving || isApplyingAllocation || !canSave}
          title={!canSave ? 'Fill in all required fields to save' : undefined}
          className={cn('mt-4 w-full rounded-full', {
            'rounded-md': hideHeader,
          })}
        >
          {isApplyingAllocation ? 'Saving...' : 'Save'}
        </Button>
      )}
    </>
  );

  if (hideHeader) {
    return <div>{formContent}</div>;
  }

  return (
    <div className="bg-background overflow-hidden rounded-xl border">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="hover:bg-muted flex w-full items-center justify-between px-5 py-4 text-left transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{label}</span>
        </div>

        <div className="flex min-w-0 max-w-[65%] items-center justify-end gap-3">
          {showPreview && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex min-w-0 max-w-full shrink cursor-default">
                    <Badge
                      variant="secondary"
                      className="hidden min-w-0 max-w-full shrink truncate font-normal sm:inline-flex"
                    >
                      {previewBadgeContent}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs"
                >
                  <p>{previewTooltipCopy}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={e => {
                      e.stopPropagation();
                      setShowFormulaSheet(true);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setShowFormulaSheet(true);
                      }
                    }}
                    className="text-muted-foreground hover:text-primary hidden shrink-0 cursor-pointer sm:inline-flex"
                  >
                    <HelpCircle className="size-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>How is this calculated?</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {!showPreview && isComplete && existingSource?.existingAssetsValue != null && (
            <Badge
              variant="secondary"
              className="hidden font-medium sm:inline-flex"
            >
              {formatCurrency(existingSource.existingAssetsValue)}
              {isSessionComplete && allocationAmount && allocationAmount > 0 && (
                <span className="text-muted-foreground ml-1">
                  (−{formatCurrency(allocationAmount)})
                </span>
              )}
            </Badge>
          )}
          {flags.selfFundingGrowthPreview === true &&
            (showPreview || (isComplete && existingSource?.existingAssetsValue != null)) && (
              <Badge
                variant="secondary"
                className="font-medium sm:hidden"
              >
                {existingSource?.existingAssetsValue != null
                  ? formatCurrency(existingSource.existingAssetsValue)
                  : currentValueFormatted}
                {isSessionComplete &&
                  allocationAmount &&
                  allocationAmount > 0 &&
                  existingSource?.existingAssetsValue != null && (
                    <span className="text-muted-foreground ml-1">
                      (−{formatCurrency(allocationAmount)})
                    </span>
                  )}
              </Badge>
            )}
          {isExpanded ? (
            <ChevronUp className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t"
          >
            {isLocked ? (
              <div className="grid grid-cols-2 gap-3 px-5 pb-5 pt-4">
                <div className="bg-muted rounded-lg p-2.5">
                  <p className="text-foreground text-xs font-medium">
                    {fieldLabels.currentValue.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.existingAssetsValue != null
                      ? formatCurrency(existingSource.existingAssetsValue)
                      : '—'}
                  </p>
                </div>
                {!isIncomeStream && (
                  <div className="bg-muted rounded-lg p-2.5">
                    <p className="text-foreground text-xs font-medium">
                      {fieldLabels.yearlyContribution.label}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-950">
                      {existingSource?.monthlyContributionAmount != null
                        ? formatCurrency(existingSource.monthlyContributionAmount * 12)
                        : '—'}
                    </p>
                  </div>
                )}
                {!isIncomeStream && (
                  <div className="bg-muted rounded-lg p-2.5">
                    <p className="text-foreground text-xs font-medium">
                      {fieldLabels.rateOfReturn.label}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-950">
                      {existingSource?.annualRateOfReturn != null
                        ? `${Math.round(existingSource.annualRateOfReturn * 100)}%`
                        : '—'}
                    </p>
                  </div>
                )}
                <div className="bg-muted rounded-lg p-2.5">
                  <p className="text-foreground text-xs font-medium">{fieldLabels.taxRate.label}</p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.gainsTaxRate != null
                      ? `${Math.round(existingSource.gainsTaxRate * 100)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="px-5 pb-5 pt-4">{formContent}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calculation Formula Sheet */}
      <Sheet
        open={showFormulaSheet}
        onOpenChange={setShowFormulaSheet}
      >
        <SheetContent
          side="right"
          className="overflow-y-auto sm:max-w-lg"
        >
          <SheetHeader>
            <SheetTitle>{formulaInfo.title}</SheetTitle>
            <SheetDescription>{label}</SheetDescription>
          </SheetHeader>

          <CalcFormulaSheetContent
            assetTypeId={assetTypeId}
            label={label}
            hasContributions={yearlyContribution != null && yearlyContribution > 0}
            hasContributionStop={contributionStopYear != null}
            hasWithdrawals={hasDrawdown === true && drawdownAmount != null && drawdownAmount > 0}
            isIncomeStream={isIncomeStream}
            showsIncomeGrowth={flags.retirementIncomeExpanded === true}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
